import React, { useState } from 'react';
import PropTypes from 'prop-types';
import get from "lodash/get";

import Toast from "shared/toast";
import LoaderOverlay from "shared/react_components/LoaderOverlay";

import CommentDisplay from './CommentDisplay';
import CommentForm from './CommentForm';
import CallReferences from './CallReferences';

const Comment = ({
  createdAt,
  editable,
  hashid,
  threadHashid,
  lastEditedAt,
  following,
  onFollowingToggle,
  onDelete,
  onSave,
  richText,
  createdByName,
  createdByDeleted,
  callRecordingRef,
  transcriptRef,
  onRemoveReferences,
  highlight,
  onAudioJump,
  focus,
  userMentionsData
}) => {
  const [editing, setEditing] = useState(false);
  const [loading, setLoading] = useState(false);

  const uiLocationIndexForReferences = () => {
    // Store call recording and transcript ui location indexes by milliseconds for better precision
    if (callRecordingRef) {
      return callRecordingRef.startTime * 1000;
    } else if (transcriptRef) {
      return transcriptRef.startTime * 1000;
    }
  };

  const referenceAttributes = () => {
    if (!hashid) {
      const uiLocationIndex = uiLocationIndexForReferences();
      return { uiLocationIndex, callRecordingRef, transcriptRef };
    } else {
      return {};
    }
  };

  const onSubmit = async (values) => {
    try {
      setLoading(true);
      await onSave({ ...referenceAttributes(), ...values });

      if (editing) {
        setEditing(false);
      }
    } catch (error) {
      let toastMessage = "An error occurred while trying to post your comment. Please try again.";
      if (get(error, "graphQLErrors[0].extensions.code") === "FORBIDDEN_HIDDEN_MEMBERSHIP") {
        toastMessage = error.graphQLErrors[0].message;
      }
      Toast.error({
        title: "Error posting comment",
        message: toastMessage
      });
    } finally {
      setLoading(false);
    }
  };

  const removeCallReferencesCallback = () => {
    if (!hashid) { // Don't allow removing call references if editing a comment
      return onRemoveReferences;
    }
  };

  const renderComment = () => {
    if (hashid && !editing) { // Existing comment
      return (
        <CommentDisplay
          createdAt={createdAt}
          editable={editable}
          hashid={hashid}
          threadHashid={threadHashid}
          lastEditedAt={lastEditedAt}
          following={following}
          onFollowingToggle={onFollowingToggle}
          onDelete={onDelete}
          onEdit={() => setEditing(true)}
          richText={richText}
          createdByName={createdByName}
          createdByDeleted={createdByDeleted}
          highlight={highlight}
        />
      );
    } else { // New comment or editing existing comment
      return (
        <CommentForm
          buttonText={editing ? "Update" : "Post"}
          editing={editing}
          hashid={hashid}
          initialText={richText}
          onCancel={() => setEditing(false)}
          onSubmit={onSubmit}
          placeholder={editing ? "" : "What's on your mind?"}
          userMentionsData={userMentionsData}
          focus={focus}
        />
      );
    }
  };

  return (
    <LoaderOverlay loading={loading}>
      {(transcriptRef || callRecordingRef) &&
        <CallReferences
          transcriptRef={transcriptRef}
          callRecordingRef={callRecordingRef}
          onAudioJump={onAudioJump}
          onRemove={removeCallReferencesCallback()}
        />
      }
      {renderComment()}
    </LoaderOverlay>
  );
};

Comment.defaultProps = {
  editable: false,
  following: false
};

Comment.propTypes = {
  /**
   * Timestamp the comment was created
   */
  createdAt: PropTypes.string,
  /**
   * Informs whether actions such as Edit and Delete are enabled
   */
  editable: PropTypes.bool,
  /**
   * The comment's id.
   * If an `id` is provided, we assume the comment record should be displayed.
   * If an `id` is not provided, we display an input form
   */
  hashid: PropTypes.string,
  /**
   * The hashid of the comment's thread.
   */
  threadHashid: PropTypes.string,
  /**
   * Human-readable time when the comment was last edited
   */
  lastEditedAt: PropTypes.string,
  /**
   * Informs whether the user is following the thread of this comment.
   */
  following: PropTypes.bool,
  /**
   * Callback for when a thread is followed.
   * It expects a Promise to be returned.
   * Takes the thread id as an argument
   */
  onFollowingToggle: PropTypes.func,
  /**
   * Callback for when a comment is deleted.
   * It expects a Promise to be returned.
   * Takes the comment id as an argument
   */
  onDelete: PropTypes.func,
  /**
   * Callback for when a new comment is created or an existing comment is edited.
   * It expects a Promise to be returned.
   * Takes in an object with 2 keys, 'hashid' and 'text'
   */
  onSave: PropTypes.func,
  /**
   * Text comment.
   * Only required for existing comments
   */
  richText: PropTypes.string,
  /**
   * Name of the person who wrote the comment.
   * Only required for existing comments
   */
  createdByName: PropTypes.string,
  /**
   * Boolean that indicates if the creator of the comment has been deleted
   * Only required for existing comments
   */
  createdByDeleted: PropTypes.bool,
  /**
   * Reference data for a call recording
   */
  callRecordingRef: PropTypes.shape({
    startTime: PropTypes.number
  }),
  /**
   * Reference data for a transcript event
   */
  transcriptRef: PropTypes.shape({
    startTime: PropTypes.number,
    text: PropTypes.string,
    speaker: PropTypes.string
  }),
  /**
   * Function that is called when comment references are to be removed
   */
  onRemoveReferences: PropTypes.func,
  /**
   * Boolean that determines if the comment is highlighted and should be scrolled into view
   */
  highlight: PropTypes.bool,
  /**
   * Function that accepts an integer argument that determines what point in the audio player to jump to.
   */
  onAudioJump: PropTypes.func,
  /**
   * Boolean that indicates if the comment form text area should be focused
   */
  focus: PropTypes.bool,
  /**
   * A function that returns a Promise that returns the array of possible users.
   * Returned objects in array must have shape { id:, display:, email:, role: }.
   * `id:` should be the object's `primaryKey`. If `role:` is "Filtered", a warning will show on the suggestion.
   * Only supported for new comments, not supported when editing comments.
   * Function should accept a single `query` arg that is a search string.
   */
  userMentionsData: PropTypes.func
};

export default Comment;
